/**
 * Platform Detection Utilities
 * Wraps Capacitor's platform detection for use throughout the app.
 */

import { Capacitor } from '@capacitor/core';

/** Returns true when running inside a native iOS or Android shell */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Returns true on native iOS */
export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

/** Returns true on native Android */
export function isAndroid(): boolean {
  return Capacitor.getPlatform() === 'android';
}

/** Returns 'ios' | 'android' | 'web' */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/** Web Push is only available in the browser (not native) */
export function supportsWebPush(): boolean {
  return (
    !isNative() &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'serviceWorker' in navigator
  );
}

/** Native push (FCM/APNs via Capacitor) is only available on native */
export function supportsNativePush(): boolean {
  return isNative();
}

/**
 * Trigger haptic feedback on native (no-op on web).
 * Lightweight — dynamically imports @capacitor/haptics only when needed.
 */
export async function hapticFeedback(
  style: 'light' | 'medium' | 'heavy' = 'light'
): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: styleMap[style] });
  } catch {
    // Haptics not available on this platform
  }
}
